import { useForm } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { bizminer_app_pillar_insight_insight_request_path } from "@/routes";
import type {
  BizminerAccount,
  BizminerPillarInsight,
} from "@/types/serializers";
import {
  type Competitor,
  CompetitorsField,
} from "../../Accounts/_components/CompetitorsField";

interface Props {
  pillar_insight: BizminerPillarInsight;
  account: BizminerAccount;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface FormData {
  insight_request: {
    timeline: string;
    additional_info: string;
    competitors: Competitor[];
  };
}

export function RequestForm({
  pillar_insight,
  account,
  onSuccess,
  onCancel,
}: Props) {
  // Store default competitors from account for reset functionality
  const defaultCompetitors: Competitor[] = account.competitors ?? [];

  const { data, setData, post, processing, errors } = useForm<FormData>({
    insight_request: {
      timeline: "",
      additional_info: "",
      competitors: [...defaultCompetitors],
    },
  });

  const prefillACME = () => {
    setData("insight_request", {
      timeline: "Q2 2025",
      additional_info:
        "We are particularly interested in understanding our competitive position in the heavy machinery segment and identifying opportunities for market expansion in the APAC region.",
      competitors: [
        {
          name: "GlobalTech Industries",
          url: "https://globaltech.example.com",
        },
        { name: "Apex Manufacturing", url: "https://apex-mfg.example.com" },
        {
          name: "Industrial Solutions Inc",
          url: "https://indsolutions.example.com",
        },
      ],
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    post(bizminer_app_pillar_insight_insight_request_path(pillar_insight.id), {
      onSuccess: () => {
        // Inertia will automatically follow the redirect from the controller
        onSuccess?.();
      },
    });
  };

  const updateCompetitors = (newCompetitors: Competitor[]) => {
    setData("insight_request", {
      ...data.insight_request,
      competitors: newCompetitors,
    });
  };

  const resetToAccountDefaults = () => {
    setData("insight_request", {
      ...data.insight_request,
      competitors: [...defaultCompetitors],
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Report Generation Request</CardTitle>
              <CardDescription>
                Please provide details for the report to generate
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Use Case (readonly) */}
          <div className="space-y-2">
            <Label htmlFor="use_case">Use Case / Analysis Area</Label>
            <Input
              id="use_case"
              value={pillar_insight.title}
              disabled
              className="bg-muted"
            />
          </div>

          {/* Timeline */}
          <div className="space-y-2">
            <Label htmlFor="timeline">
              Timeline <span className="text-destructive">*</span>
            </Label>
            <Input
              id="timeline"
              placeholder="e.g., Q1 2025, By end of year"
              value={data.insight_request.timeline}
              onChange={(e) =>
                setData("insight_request", {
                  ...data.insight_request,
                  timeline: e.target.value,
                })
              }
              required
            />
            {errors["insight_request.timeline"] && (
              <p className="text-sm text-destructive">
                {errors["insight_request.timeline"]}
              </p>
            )}
          </div>

          {/* Competitors */}
          <CompetitorsField
            competitors={data.insight_request.competitors}
            onUpdate={updateCompetitors}
            errors={errors["insight_request.competitors"]}
            defaultCompetitors={defaultCompetitors}
            onReset={resetToAccountDefaults}
          />

          {/* Additional Information */}
          <div className="space-y-2">
            <Label htmlFor="additional_info">Additional Information</Label>
            <Textarea
              id="additional_info"
              placeholder="Any additional context or specific areas you'd like us to focus on..."
              rows={4}
              value={data.insight_request.additional_info}
              onChange={(e) =>
                setData("insight_request", {
                  ...data.insight_request,
                  additional_info: e.target.value,
                })
              }
            />
            {errors["insight_request.additional_info"] && (
              <p className="text-sm text-destructive">
                {errors["insight_request.additional_info"]}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-4 pt-4">
            {import.meta.env.DEV && (
              <Button type="button" variant="destructive" onClick={prefillACME}>
                Prefill ACME
              </Button>
            )}
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? "Submitting..." : "Submit Analysis Request"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}
